import { Component, computed, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigateService } from '@shared/services/navigate.service';
import {
  BasePanelChangeEvent,
  BasePanelChangeEventType,
  BasePanelService
} from '@shared/modules/base-panel/services/base-panel.service';
import { Subscription } from 'rxjs';
import { StorageService } from '@core/services/storage.service';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { TabLink } from '@shared/interfaces/menu/tab-link.interface';
import { HeaderConfig } from '@shared/configs/header.config';
import { CheckPermission } from '@core/permissions/check-permission';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { PermissionsGroups } from '@core/permissions/permissions.group';
import { Employee } from '@shared/models/employee.model';
import { ContactController } from '@modules/contacts/shared/controllers/contact.controller';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import { EventSidenavController } from '@shared/modules/event-sidenav/controllers/event-sidenav.controller';
import { MailingController } from '@shared/modules/mailings/shared/controllers/mailing.controller';
import { ChangesSidenavController } from '@shared/modules/task-sidenav/controllers/changes-sidenav.controller';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { HeaderLogoComponent } from './components/header-logo/header-logo.component';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { HeaderService } from './services/header.service';
import { HeaderHamburgerComponent } from './components/header-hamburger/header-hamburger.component';
import { PermissionDirectiveModule } from '@shared/modules/permission-directive/permission-directive.module';
import { UIModule } from '@shared/modules/ui/ui.module';
import { HeaderActionMenuComponent } from './components/header-action-menu/header-action-menu.component';
import { HeaderActionMenuUserComponent } from './components/header-action-menu/components/header-action-menu-user/header-action-menu-user.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'intilio-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderLogoComponent,
    SvgIconComponent,
    TranslateModule,
    MatMenuModule,
    HeaderHamburgerComponent,
    PermissionDirectiveModule,
    UIModule,
    HeaderActionMenuComponent,
    HeaderActionMenuUserComponent,
    MatTooltipModule
  ],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('200ms', style({ opacity: 0 }))])
    ]),
    trigger('openClose', [
      state('true', style({ width: '230px' })),
      state('false', style({ width: '68px' })),
      transition('false <=> true', animate(200))
    ])
  ],
  providers: [HeaderService]
})
export class HeaderComponent implements OnInit, OnDestroy {
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  PermissionsGroups = PermissionsGroups;

  sub: Subscription;
  tabLinks: TabLink[] = [];

  @ViewChild('header') header: ElementRef;

  employee: Employee;

  protected isFullyExpanded = computed(
    () => this.basePanelService.isExpanded() || this.basePanelService.sideManuallyExpanded()
  );

  protected isCollapsed = computed(() => !this.isFullyExpanded());

  constructor(
    public n: NavigateService,
    protected basePanelService: BasePanelService,
    public store: StorageService,
    public headerService: HeaderService
  ) {
    this.configureNavigation();

    this.employee = this.store.Employee;
  }

  ngOnInit(): void {
    this.listenChange();
  }

  handleHeaderMouseEnter(): void {
    this.basePanelService.handleHeaderMouseEnter();
  }

  listenChange() {
    this.basePanelService.panelChangeEmitter.subscribe((event: BasePanelChangeEvent) => {
      switch (event.type) {
        case BasePanelChangeEventType.HIDE_TOP_MENU:
          this.header.nativeElement.classList.add('hide');
          break;
        case BasePanelChangeEventType.SHOW_TOP_MENU:
          this.header.nativeElement.classList.remove('hide');
          break;
      }
    });
  }

  configureNavigation() {
    const ctrl = new CheckPermission(null);
    Object.values(HeaderConfig).map((tabLink: TabLink) => {
      if (!tabLink.permissionAction || !isNotNullOrUndefined(tabLink.permissionGroup)) {
        this.tabLinks = [];
        return console.error('ERROR Error: There is no permissions declared for header navigation!');
      }

      if (
        (tabLink.permissionGroup === PermissionsGroups.CONTACTS ||
          tabLink.permissionGroup === PermissionsGroups.PROJECTS) &&
        tabLink.permissionAction === 'LIST'
      ) {
        const employee = this.store.Employee;
        ctrl.setPermissionDecorator({
          group: tabLink.permissionGroup,
          action: tabLink.permissionAction,
          objectCreatorId: employee?.hasResponsibleProjects ? employee.id : null
        });
      } else {
        ctrl.setPermissionDecorator({ group: tabLink.permissionGroup, action: tabLink.permissionAction });
      }

      if (ctrl.check()) {
        this.tabLinks.push(tabLink);
      }
    });
  }

  ngOnDestroy() {
    this.sub ? this.sub.unsubscribe() : null;
  }

  createTask() {
    const ctrl = new TaskSidenavController();
    ctrl.addTask();
    this.headerService.closeMenu();
  }

  createChange() {
    const ctrl = new ChangesSidenavController();
    ctrl.addChange(null);
    this.headerService.closeMenu();
  }

  createEvent() {
    const ctrl = new EventSidenavController();
    ctrl.createEvent();
    this.headerService.closeMenu();
  }

  createContact() {
    const ctrl = new ContactController();
    ctrl.add();
  }

  createProjectDraft() {
    const ctrl = new ProjectController();
    ctrl.add();
    this.headerService.closeMenu();
  }

  createEmail() {
    const ctrl = new MailingController();
    ctrl.openMailingSelection('');
    this.headerService.closeMenu();
  }

  isTimelineRoute(tabLink: TabLink) {
    const isProjects = window.location.href.indexOf('projects') !== -1;
    if (tabLink.link === 'projects-list' && isProjects) return true;
    return false;
  }
}
